import { FileDown, Linkedin, Github, Mail, UserRoundPlus } from "lucide-react";
import { siteConfig } from "../config";
import { useSafeLink, FadeIn } from "./common";
import { CategoryBackground } from "./SciBackground";

const BUTTONS = [
  { key: "resume", label: "Download Resume", icon: FileDown, url: () => siteConfig.resumeUrl, download: () => siteConfig.resumeDownloadFilename, primary: true },
  { key: "vcard", label: "Save Contact", icon: UserRoundPlus, url: () => siteConfig.vcardUrl, download: () => siteConfig.vcardDownloadFilename },
  { key: "linkedin", label: "LinkedIn", icon: Linkedin, url: () => siteConfig.linkedinUrl },
  { key: "github", label: "GitHub", icon: Github, url: () => siteConfig.githubUrl, hidden: () => !siteConfig.githubEnabled },
  { key: "email", label: "Email", icon: Mail, url: () => (siteConfig.emailAddress ? `mailto:${siteConfig.emailAddress}` : "") },
];

export default function Contact() {
  const safeLink = useSafeLink();
  return (
    <section id="contact" className="relative overflow-hidden scroll-mt-16" data-testid="contact-section">
      <CategoryBackground variant="contact" accent="cyan" accent2="teal" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-24">
      <FadeIn>
        <div className="panel p-8 lg:p-14 sci-grid text-center">
          <p className="overline-label">Contact</p>
          <h2 className="font-sans tracking-tight text-4xl sm:text-5xl text-paper mt-4 max-w-2xl mx-auto">
            Interested in Manufacturing, Product Quality or Automation Problems?
          </h2>
          <p className="text-steel text-base mt-5 max-w-2xl mx-auto leading-relaxed">
            I streamline fragmented manufacturing data, automate evidence gathering and implement controlled process improvements across digital
            manufacturing, Product Quality, QMS, MSAT and operational excellence work.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-8">
            {BUTTONS.filter((b) => !(b.hidden && b.hidden())).map((b) => {
              const Icon = b.icon;
              return (
                <button
                  key={b.key}
                  data-testid={`contact-${b.key}-button`}
                  onClick={() => safeLink(b.url(), b.label === "Download Resume" ? "Resume" : b.label, b.download ? b.download() : undefined)}
                  className={`flex items-center gap-2 font-sans text-sm px-5 py-3 rounded-sm transition-colors ${
                    b.primary
                      ? "bg-cyan text-navy font-semibold hover:bg-pale"
                      : "border border-teal/40 text-pale/80 hover:border-cyan/60 hover:text-paper"
                  }`}
                >
                  <Icon className="w-4 h-4" aria-hidden="true" /> {b.label}
                </button>
              );
            })}
          </div>
          <div className="mt-7 mx-auto max-w-xl border border-cyan/20 bg-navy/70 rounded-md p-4 flex flex-col sm:flex-row items-center justify-center gap-4" data-testid="contact-quick-save">
            <a
              href={siteConfig.portfolioBaseUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-portfolio-qr-link"
              className="shrink-0 group"
              aria-label="Open portfolio from QR code"
            >
              <img
                src={siteConfig.portfolioQrUrl}
                alt={`QR code for ${siteConfig.domainDisplay}`}
                data-testid="contact-portfolio-qr"
                className="w-24 h-24 rounded-sm border border-cyan/30 bg-white p-1 transition-transform group-hover:scale-[1.03]"
              />
            </a>
            <div className="text-center sm:text-left">
              <p className="overline-label">Save / Share Contact</p>
              <p className="text-sm text-pale/85 mt-2 leading-relaxed">
                Scan the QR code for the portfolio, or use Save Contact to download a recruiter-ready vCard.
              </p>
              <p className="font-mono text-xs text-cyan mt-2" data-testid="contact-domain-display">{siteConfig.domainDisplay}</p>
            </div>
          </div>
          {siteConfig.location && <p className="font-mono text-xs text-steel mt-6">{siteConfig.location}</p>}
          <p className="font-mono text-[11px] text-cyan/70 mt-4 tracking-wide" data-testid="contact-philosophy">
            Understand the process. Verify the evidence. Protect data integrity. Control the change. Automate what should not be manual.
          </p>
        </div>
      </FadeIn>
      </div>
    </section>
  );
}
